
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { LoginForm } from "@/components/AreaPaciente/LoginForm";
import { HeroSection } from "@/components/AreaPaciente/HeroSection";
import { ContactSection } from "@/components/AreaPaciente/ContactSection";
import { ReactNode } from "react";

interface AreaPacienteLayoutProps {
  children?: ReactNode;
  isAdminPanel: boolean;
  showLoginForm?: boolean;
  loginFormProps?: {
    email: string;
    setEmail: (email: string) => void;
    password: string;
    setPassword: (password: string) => void;
    isLoading: boolean;
    onSubmit: (e: React.FormEvent) => void;
  };
}

// ─── Admin login — full-page redesign ────────────────────────────────────────
function AdminLoginPage({ loginFormProps }: { loginFormProps: NonNullable<AreaPacienteLayoutProps["loginFormProps"]> }) {
  return (
    <div className="min-h-screen flex">

      {/* ── Left panel — brand ── */}
      <div
        className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, #1a4d30 0%, #1F5938 60%, #17472e 100%)" }}
      >
        {/* Background decoration */}
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-8 w-48 h-48 rounded-full bg-white/[0.03]" />

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center text-xl shadow-md">
              ⚜️
            </div>
            <div>
              <p className="text-white font-bold text-base font-primary leading-tight">Clínica Equilíbrio</p>
              <p className="text-white/40 text-[11px] uppercase tracking-widest">Nutrição Clínica</p>
            </div>
          </div>
        </div>

        {/* Main copy */}
        <div className="relative z-10 space-y-5">
          <div>
            <p className="text-white/50 text-sm font-primary uppercase tracking-[0.15em] mb-3">Painel Administrativo</p>
            <h1 className="text-4xl font-extrabold text-white font-primary leading-tight">
              Gerencie seu<br />
              <span className="text-white/60">site com</span><br />
              facilidade.
            </h1>
          </div>
          <p className="text-white/50 text-sm leading-relaxed font-primary max-w-xs">
            Controle conteúdos, depoimentos, blog, agendamentos e muito mais em um só lugar.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-2 pt-2">
            {["Blog", "Serviços", "Depoimentos", "Imprensa", "Financeiro"].map(f => (
              <span key={f} className="text-xs text-white/50 bg-white/10 px-3 py-1 rounded-full font-primary">
                {f}
              </span>
            ))}
          </div>
        </div>

        {/* Footer credit */}
        <div className="relative z-10">
          <p className="text-white/20 text-[10px] uppercase tracking-widest font-primary">
            Dra. Brenda Ramos · CRN/3-63492
          </p>
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-gray-50 min-h-screen">

        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-xl bg-brand-primary flex items-center justify-center text-lg">
            ⚜️
          </div>
          <div>
            <p className="text-brand-primary font-bold text-sm font-primary leading-tight">Clínica Equilíbrio</p>
            <p className="text-gray-400 text-[10px] uppercase tracking-widest">Painel Administrativo</p>
          </div>
        </div>

        <div className="w-full max-w-[400px]">
          {/* Form header */}
          <div className="mb-8">
            <h2 className="text-2xl font-extrabold text-gray-800 font-primary">Entrar</h2>
            <p className="text-gray-400 text-sm mt-1 font-primary">Acesse o painel de gestão da clínica</p>
          </div>

          {/* Inline form (no Card wrapper) */}
          <AdminLoginForm {...loginFormProps} />
        </div>
      </div>
    </div>
  );
}

// ─── Inline login form (clean, no Card) ──────────────────────────────────────
function AdminLoginForm({
  email, setEmail, password, setPassword, isLoading, onSubmit,
}: NonNullable<AreaPacienteLayoutProps["loginFormProps"]>) {
  const [showPw, setShowPw] = useState(false);
  const [view, setView]     = useState<"login" | "forgot" | "sent">("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError]     = useState("");
  const { authApi } = useAuthApi();

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError("");
    setForgotLoading(true);
    try {
      await authApi.forgotPassword(forgotEmail);
      setView("sent");
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : "Erro ao enviar email.");
    } finally {
      setForgotLoading(false);
    }
  };

  if (view === "sent") {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-brand-primary/10 flex items-center justify-center mx-auto">
          <span className="text-3xl">✉️</span>
        </div>
        <h3 className="font-bold text-gray-800 font-primary">Email enviado!</h3>
        <p className="text-gray-500 text-sm font-primary">
          Se <strong>{forgotEmail}</strong> estiver cadastrado, você receberá as instruções em breve.
        </p>
        <button onClick={() => { setView("login"); setForgotEmail(""); }}
          className="text-brand-primary font-semibold text-sm hover:underline font-primary">
          Voltar ao login
        </button>
      </div>
    );
  }

  if (view === "forgot") {
    return (
      <form onSubmit={handleForgot} className="space-y-5">
        <div>
          <h3 className="font-bold text-gray-800 font-primary mb-1">Recuperar senha</h3>
          <p className="text-gray-400 text-sm font-primary">Informe seu email para receber o link de redefinição.</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide font-primary">Email</label>
          <input
            type="email"
            required
            value={forgotEmail}
            onChange={e => setForgotEmail(e.target.value)}
            placeholder="seu@email.com"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 bg-white outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/15 transition font-primary"
          />
        </div>
        {forgotError && <p className="text-red-500 text-xs font-primary">{forgotError}</p>}
        <button type="submit" disabled={forgotLoading}
          className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-bold py-3.5 rounded-xl text-sm transition-all disabled:opacity-60 font-primary">
          {forgotLoading ? "Enviando..." : "Enviar link"}
        </button>
        <button type="button" onClick={() => setView("login")}
          className="w-full text-center text-gray-400 text-sm hover:text-brand-primary transition-colors font-primary">
          ← Voltar ao login
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="text-xs font-semibold text-gray-600 uppercase tracking-wide font-primary">
          Email
        </label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="seu@email.com"
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 bg-white outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/15 transition font-primary"
        />
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label htmlFor="password" className="text-xs font-semibold text-gray-600 uppercase tracking-wide font-primary">
            Senha
          </label>
          <button type="button" onClick={() => setView("forgot")}
            className="text-xs text-brand-primary hover:underline font-primary">
            Esqueci minha senha
          </button>
        </div>
        <div className="relative">
          <input
            id="password"
            type={showPw ? "text" : "password"}
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-11 text-sm text-gray-800 bg-white outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/15 transition font-primary"
          />
          <button type="button" onClick={() => setShowPw(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-bold py-3.5 rounded-xl text-sm transition-all disabled:opacity-60 font-primary flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <LogIn className="w-4 h-4" />
        )}
        {isLoading ? "Entrando..." : "Entrar no Painel"}
      </button>

      <p className="text-center text-xs text-gray-400 font-primary pt-2">
        Acesso exclusivo para administradores
      </p>
    </form>
  );
}

// Lazy imports for AdminLoginForm internals
import { useState } from "react";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { authApi as rawAuthApi } from "@/integrations/mysql/client";
function useAuthApi() { return { authApi: rawAuthApi }; }

// ─── Main layout ──────────────────────────────────────────────────────────────
export function AreaPacienteLayout({
  children, isAdminPanel, showLoginForm = false, loginFormProps,
}: AreaPacienteLayoutProps) {

  // Admin login: completely different full-page layout
  if (isAdminPanel && showLoginForm && loginFormProps) {
    return <AdminLoginPage loginFormProps={loginFormProps} />;
  }

  if (children) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <HeroSection isAdminPanel={isAdminPanel} />

      {showLoginForm && loginFormProps && (
        <section className="py-16">
          <div className="container mx-auto px-6">
            <LoginForm {...loginFormProps} />
            {!isAdminPanel && (
              <div className="text-center mt-4">
                <p className="text-sm text-gray-600">
                  Ainda não tem uma conta? Entre em contato conosco para criar sua conta.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {!isAdminPanel && <ContactSection />}
      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
